"use client";

import React, { useState, useEffect, useMemo, useRef } from "react";
import { cn } from "@/lib/utils";
import { PortableTextBlock } from "sanity";
import { GROQImage } from "@/sanity/lib/definitions";
import Image from "@/helpers/Image";
import { string } from "zod";
import { padStart } from "lodash-es";
import { PortableText } from "next-sanity";
import { richTextComponent } from "@/sanity/sanityPortableText";
import DividerGreenLine from "./DividerGreenLine";
import Autoplay from "embla-carousel-autoplay";
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from "@/shadcn/ui/carousel";

interface TimedCardsProps {
  cards: {
    title: PortableTextBlock[];
    text: PortableTextBlock[];
    bullets: PortableTextBlock[];
    image: GROQImage;
    imageMobile: GROQImage;
  }[];
  timePerCard: number;
  className?: string;
}

function TimedCard({
  card,
  index,
  timeProgress,
}: {
  card: {
    title: PortableTextBlock[];
    text: PortableTextBlock[];
    bullets: PortableTextBlock[];
    image: GROQImage;
    imageMobile: GROQImage;
  };
  index: number;
  timeProgress?: number;
}) {
  return (
    <div className="w-full h-full border-t-[0.25rem] border-[#D9F9B9] relative lg:min-h-[70.5rem] bg-[#0A1A1D] flex flex-col lg:h-full overflow-clip">
      <Image
        src={card.image}
        alt="card-bg"
        className="hidden lg:block absolute z-0 inset-0 !w-full !h-full"
      />
      <Image src={card.imageMobile} alt="card-bg" className="lg:hidden" />
      <span className="hidden lg:block absolute z-0 left-0 bottom-0 text-[#D9F9B9] opacity-[0.02] font-ivy text-[50rem] leading-none">
        {padStart((index + 1).toString(), 2, "0")}
      </span>

      <div className="flex flex-col lg:justify-between p-[2rem] lg:px-[7rem] lg:py-[9rem] relative z-[1] w-full lg:w-[90.25rem] lg:h-full">
        <div className="flex flex-col">
          <div className="flex flex-col lg:flex-row">
            <span className="shrink-0 text-[4rem] lg:text-[5rem] font-geistmono text-[#D9F9B9] font-semibold leading-[165%]">
              {index + 1}.
            </span>
            <div className="richtext richtext-start prose-headings:leading-[150%] prose-headings:not-italic prose-headings:text-white prose-headings:text-[4rem] lg:prose-headings:text-[5rem] prose-headings:font-ivy prose-p:text-white/60 prose-headings:w-[95%] lg:prose-headings:w-full mb-0">
              <PortableText value={card.title} components={richTextComponent} />
            </div>
          </div>
          <div className="richtext richtext-start prose-headings:leading-[150%] prose-headings:not-italic prose-headings:text-white prose-headings:text-[4rem] lg:prose-headings:text-[5rem] prose-headings:font-ivy prose-p:text-white/60 prose-p:text-[2rem] lg:prose-p:text-[2.25rem] mb-0">
            <PortableText value={card.text} components={richTextComponent} />
          </div>
          <div className="h-[0.125rem] relative bg-white bg-opacity-20 w-full mt-[2rem] mb-[3rem]">
            <div className="absolute h-full left-0 translate-x-0 w-[12.20363rem] lg:w-[21.41238rem] bg-[#D9F9B9]"></div>
          </div>
        </div>
        <div
          className="richtext richtext-start
          prose-p:text-white/60 prose-p:text-[2rem] lg:prose-p:text-[2.25rem] prose-p:font-light prose-p:leading-[150%]
          prose-li:marker:text-[#D9F9B9] prose-ul:[list-style-type:square] prose-ul:ps-[1.5rem]
          prose-li:text-white/90 prose-li:text-[2rem] lg:prose-li:text-[2.25rem] prose-li:font-light prose-li:leading-[150%]"
        >
          <PortableText value={card.bullets} components={richTextComponent} />
        </div>
      </div>
    </div>
  );
}

export default function TimedCards({
  cards,
  timePerCard,
  className = "",
}: TimedCardsProps) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  useEffect(() => {
    if (!api) return;

    const onSelect = () => {
      setCurrent(api.selectedScrollSnap());
      setCanPrev(api.canScrollPrev());
      setCanNext(api.canScrollNext());
    };

    onSelect();
    api.on("select", onSelect);
    api.on("reInit", onSelect);
    return () => {
      api.off("select", onSelect);
      api.off("reInit", onSelect);
    };
  }, [api]);

  return (
    <div className={cn("relative w-full flex flex-col gap-[2rem]", className)}>
      {/*<div className="relative w-full flex flex-col gap-[4rem] lg:gap-[7rem]">
        {cards.map((card, index) => (
          <TimedCard key={"tcard" + index} index={index} card={card} />
        ))}
      </div>*/}
      <Carousel
        className="w-full [&_>_div]:!overflow-visible"
        setApi={setApi}
        opts={{
          align: "center",
          skipSnaps: false,
        }}
        plugins={[
          Autoplay({ playOnInit: true, delay: 7000, stopOnInteraction: false }),
        ]}
      >
        <CarouselContent className="w-full gap-x-[1rem]">
          {cards.map((card, index) => (
            <CarouselItem
              key={index}
              className="p-0 basis-[44.75rem] lg:basis-[150rem] lg:min-h-[90.5rem]"
            >
              <TimedCard index={index} card={card} />
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      <div className="flex items-center justify-between flex-shrink-0">
        {/* Progress Bar */}
        <div className="flex items-center w-full ">
          <div className="flex w-full max-w-[60rem] gap-[1rem]">
            {cards.map((_, index) => (
              <div
                key={`progress-${index}`}
                className={cn(
                  "h-[0.5rem] bg-[#112628] transition-all duration-300 ease-out",
                  index === current
                    ? "opacity-100 w-[12.5rem] lg:w-[21.375rem]"
                    : "flex-shrink-0 w-[5rem] lg:w-[6rem] opacity-50",
                )}
              />
            ))}
          </div>
        </div>
        <div
          className={cn(
            "flex justify-end gap-[1rem] px-[1.25rem] py-[1.125rem] relative border-[2px] border-white rounded-full",
          )}
        >
          <button
            onClick={() => api?.scrollPrev()}
            className="w-[6rem] h-[6rem] flex-shrink-0 rounded-full flex items-center justify-center bg-[#202223] transition-all text-white disabled:bg-[#202223]/10 disabled:text-[#202223] disabled:cursor-not-allowed"
            disabled={!api || !canPrev}
            aria-label="Previous slide"
          >
            <svg
              viewBox="0 0 12 20"
              xmlns="http://www.w3.org/2000/svg"
              className="w-[1.25rem] h-auto flex-shrink-0 -translate-x-[.125rem]"
            >
              <path
                d="M2.47955e-05 10L10 20L11.4 18.6L2.80002 10L11.4 1.4L10 0L2.47955e-05 10Z"
                fill="currentColor"
              />
            </svg>
          </button>

          <button
            onClick={() => api?.scrollNext()}
            className="w-[6rem] h-[6rem] flex-shrink-0 rounded-full flex items-center justify-center bg-[#202223] transition-all text-white disabled:bg-[#202223]/10 disabled:text-[#202223] disabled:cursor-not-allowed"
            disabled={!api || !canNext}
            aria-label="Next slide"
          >
            <svg
              viewBox="0 0 12 20"
              xmlns="http://www.w3.org/2000/svg"
              className="rotate-180 w-[1.25rem] h-auto flex-shrink-0 translate-x-[.125rem]"
            >
              <path
                d="M2.47955e-05 10L10 20L11.4 18.6L2.80002 10L11.4 1.4L10 0L2.47955e-05 10Z"
                fill="currentColor"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
